// Tenant-ops wire types — snake_case, mirroring the console API (docs/api-tenant-ops.md) and the DTOs the
// billing / messaging admin proxies stream through verbatim (Billing.Contracts, Messaging.Contracts).
//
// Units (CONTROL_PLANE_CONTRACTS §0.1): every `*_credits` field is integer MICRO-credits; money is
// `*_minor` + an ISO currency; rates are basis points. `null` = not known / not collected — never 0.

// ─── Environments (§2) ──────────────────────────────────────────────────────

export interface EnvironmentView {
  key: string;
  label: string;
  enabled: boolean;
  services: Record<string, string>;
  /** Presence only — the key itself (or its ciphertext) is never in any response (CP4). */
  has_internal_api_key: boolean;
  key_set_at: string | null;
  postgres_container: string | null;
  compose_project: string | null;
  public_host_suffix: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface EnvironmentList {
  environments: EnvironmentView[];
}

export interface CreateEnvironmentBody {
  key: string;
  label: string;
  enabled: boolean;
  services: Record<string, string>;
  internal_api_key?: string;
  postgres_container?: string;
  compose_project?: string;
  public_host_suffix?: string;
}

export interface UpdateEnvironmentBody {
  label?: string;
  enabled?: boolean;
  /** Replaces the WHOLE map when present. */
  services?: Record<string, string>;
  postgres_container?: string;
  compose_project?: string;
  public_host_suffix?: string;
  /** Required (re-entered, then stored) when a service points at an address the stored key was never sent to. */
  internal_api_key?: string;
}

export interface ServiceProbe {
  service: string;
  reachable: boolean;
  status: number | null;
  reason: string | null;
  latency_ms: number | null;
}

export interface EnvironmentProbeResult {
  environment: string;
  services: ServiceProbe[];
}

/** The service keys an environment can map (docs §2). */
export const PLATFORM_SERVICE_KEYS = [
  'iam', 'billing', 'messaging', 'salesiq', 'metadata', 'settings', 'workflow', 'files', 'mail',
  'timeline', 'notify', 'telephony', 'reporting', 'ai', 'agents', 'proofing', 'vault', 'desk',
  'audit', 'gateway',
] as const;

// ─── Import keys (§3) ───────────────────────────────────────────────────────

export interface ImportKeyView {
  id: string;
  label: string;
  scope: string;
  /** The environment a tenant-importing key reports for; null = unbound (hosts-only keys). */
  environment?: string | null;
  display: string;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ImportKeyList {
  keys: ImportKeyView[];
}

/** The ONE response that carries the plaintext `cpik_…` key — shown once, never kept. */
export interface IssuedImportKey {
  key: ImportKeyView;
  import_key: string;
}

// ─── Provisioning (§4) ──────────────────────────────────────────────────────

/**
 * `done_with_warnings`: finished (tenant live, parity green) but a step ended — skipped, or done (`iam.create`) —
 * with a WARNING reason (§4.1, §10).
 */
export type JobStatus = 'pending' | 'running' | 'failed' | 'done' | 'done_with_warnings' | 'cancelled';
/** `awaiting`: the service accepted the step and finishes async work; later steps run on meanwhile (§4.6). */
export type StepStatus = 'pending' | 'running' | 'waiting_retry' | 'awaiting' | 'gated' | 'done' | 'skipped' | 'failed' | 'cancelled';

/** One reason a finished job is `done_with_warnings` — each is retryable on its own. */
export interface JobWarning {
  step: string;
  code: string;
  message: string;
}

export interface ActorView {
  id: string;
  name: string;
}

export interface GrantOverrides {
  ai_credits: number | null;
  messaging_credits: number | null;
}

export interface StepView {
  key: string;
  ordinal: number;
  kind: string;
  app: string | null;
  status: StepStatus;
  attempts: number;
  next_attempt_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  http_status: number | null;
  /**
   * UPPER_SNAKE failure code, or a lower_snake reason: on a skipped step (`unlimited_plan`, `endpoint_absent`, …) or —
   * `iam.create` only — a warning on a DONE step (`tenant_db_ready_publish_failed`, `iam_step_failed`).
   */
  error_code: string | null;
  error: string | null;
  request_hash: string | null;
  idempotent: boolean;
  /** An awaiting step's deadline — the wait gives up at this instant (kept after it resolves). */
  awaiting_until?: string | null;
  /** True = ended (skipped, or done) with a WARNING reason (listed in the job's `warnings`). */
  warning?: boolean;
  /** A digest, never a response body. `credits.ai` reports its units here (§4.5). */
  response_summary: Record<string, unknown> | null;
}

export interface JobView {
  id: string;
  environment: string;
  kind: 'tavi' | 'external' | string;
  tenant_id: string;
  db_short: string;
  label: string;
  use_type: string;
  founder_email: string | null;
  generate_password: boolean;
  industry_pack: string;
  industry_pack_version: number | null;
  plan_code: string;
  apps: string[];
  hosts: string[];
  grant_overrides: GrantOverrides | null;
  status: JobStatus;
  current_step: string | null;
  created_by: ActorView;
  acting_admin: ActorView;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  cancelled_at: string | null;
  /** A generated password is waiting to be revealed (once). */
  password_available: boolean;
  password_generated: boolean;
  password_revealed_at: string | null;
  result: Record<string, unknown> | null;
  steps?: StepView[];
  /** Detail read only (null on list rows): what made the job `done_with_warnings`. */
  warnings?: JobWarning[] | null;
  /** create (a setup) · delete (a tenant deletion) · discard (removing what a cancelled setup created). */
  op?: 'create' | 'delete' | 'discard' | string;
  reason?: string | null;
  /** A discard job's cancelled setup job. */
  source_job_id?: string | null;
  /** The tenant this setup created was removed — its short name is free again. */
  tenant_deleted_at?: string | null;
  /** Only on a cancel answer: the discard that removes what the cancelled setup created (or why it did not start). */
  tenant_removal?: TenantRemovalLink | null;
}

export interface TenantRemovalLink {
  job_id: string | null;
  status: string;
  code?: string | null;
  message?: string | null;
}

export interface RemovalApproval {
  type: 'approve' | 'confirm' | 'reject' | string;
  actor_id: string;
  actor_name: string;
  at: string;
  note: string | null;
}

export interface RemovalBackup {
  status: 'queued' | 'running' | 'done' | 'failed' | string;
  runner: string | null;
  created_at: string;
  claimed_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  result: { backup_ref?: string; backup_bytes?: number; restore_ok?: boolean; databases?: string[]; path?: string } | null;
  error: string | null;
}

/** `GET /provisioning/jobs/{id}/removal` — what a deletion waits for and what was decided. */
export interface RemovalDetail {
  job_id: string;
  op: string;
  db_short: string;
  requested_by_id: string;
  requested_by_name: string;
  reason: string | null;
  source_job_id: string | null;
  approvals: RemovalApproval[];
  backup: RemovalBackup | null;
  /** The decision an owner can make now. */
  waiting_for: 'approve' | 'confirm' | 'none' | string;
}

export interface JobList {
  jobs: JobView[];
  next_cursor: string | null;
}

export interface CreateJobBody {
  environment: string;
  kind: 'tavi' | 'external';
  label: string;
  db_short: string;
  use_type?: 'team' | 'personal';
  founder_email?: string;
  founder_first_name?: string;
  founder_last_name?: string;
  generate_password?: boolean;
  industry_pack?: string;
  plan_code: string;
  apps?: string[];
  hosts?: string[];
  /** MICRO-credits, sent verbatim (§4.5). */
  grant_overrides?: { ai_credits?: number; messaging_credits?: number };
  grant_reason?: string;
  /** "Add sample records" — false leaves the pack's industry.records step out of the job. Omitted = true. */
  sample_records?: boolean;
}

/** `reveal-password` — the only place a generated founder password ever appears, once. */
export interface RevealedPassword {
  tenant_id: string;
  founder_email: string | null;
  password: string;
}

export interface CatalogRow {
  ordinal: number;
  key: string;
  /** Which jobs use the row: create (setup) · delete · discard. Absent = create. */
  op?: 'create' | 'delete' | 'discard' | string;
  kind: string;
  app: string | null;
  method: string | null;
  url_template: string | null;
  body_template: unknown;
  enabled: boolean;
  skip_if_absent: boolean;
  job_kinds: string[] | null;
  key_header: string | null;
  timeout_seconds: number;
  notes: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface CatalogView {
  environment: string;
  template_vocabulary: string[];
  steps: CatalogRow[];
}

export interface LocalizedText {
  en?: string | null;
  ar?: string | null;
}

export interface PackSummary {
  key: string;
  version: number | null;
  label: LocalizedText | null;
  description: LocalizedText | null;
  valid: boolean;
  errors: string[];
  /** Replaced by other packs: its jobs still retry, but no new job may use it (hidden in the create form). */
  retired: boolean;
  sections: string[];
  seeder: string | null;
  features: string[];
  relabels: string[];
  extra_modules: string[];
  ai_industry: string | null;
  intent_taxonomy: string[];
  skills_pack: string | null;
  departments: LocalizedText[];
  agents_optional: boolean | null;
  notes: string[];
}

export interface PackList {
  packs: PackSummary[];
}

export interface PackDetail {
  summary: PackSummary;
  /** The whole pack document (code-as-data) — rendered read-only. */
  pack: Record<string, unknown> | null;
}

// ─── Environment tenants + footprint (§5) ──────────────────────────────────

export interface EnvironmentTenant {
  tenant_id: string;
  name: string | null;
  environment: string;
  plan_code: string | null;
  subscription_status: string | null;
  user_count: number | null;
  active_user_count: number | null;
  storage_used_bytes: number | null;
  storage_file_count: number | null;
  last_activity_at: string | null;
  is_internal: boolean;
  created_at: string;
  synced_at: string;
  provisioning_job_id: string | null;
  provisioning_status: string | null;
  /** Absent from an older console API. */
  footprint?: EnvironmentTenantFootprint | null;
}

/**
 * The tenants list's footprint columns: `db_bytes` is MEASURED (latest sample per database; null = not collected);
 * `cpu_cores` / `memory_bytes` are ESTIMATED over `period_days` by the tenant page's method (CP3).
 */
export interface EnvironmentTenantFootprint {
  db_bytes: number | null;
  db_sampled_at: string | null;
  cpu_cores: BasisValue;
  memory_bytes: BasisValue;
  period_days: number;
}

export interface EnvironmentTenantList {
  environment: string;
  tenants: EnvironmentTenant[];
}

/** A day's value; `null` = no sample that day (a GAP), never zero. */
export interface TrendPoint {
  day: string;
  value: number | null;
}

export interface DatabaseFootprint {
  name: string;
  kind: string;
  size_bytes: number | null;
  connections: number | null;
  sampled_at: string | null;
  top_relations: { name: string; bytes: number }[] | null;
  size_trend: TrendPoint[];
}

export interface Footprint {
  tenant_id: string;
  environment: string;
  name: string | null;
  generated_at: string;
  synced_at: string;
  period: { from: string; to: string; days: number };
  storage: {
    basis: 'measured';
    total_db_bytes: number | null;
    databases: DatabaseFootprint[];
    objects: {
      bytes: number | null;
      file_count: number | null;
      sampled_at: string | null;
      bytes_trend: TrendPoint[];
      file_count_trend: TrendPoint[];
    };
  };
  database_activity: {
    basis: 'measured';
    days: DbActivityDay[];
  };
  traffic: {
    basis: 'measured';
    available: boolean;
    days: TrafficDay[];
    by_app: TrafficByApp[];
  };
  compute_share: ComputeShare;
}

export interface DbActivityDay {
  day: string;
  /** False = at least one database had no sample at one end of the day (a partial sum). */
  complete: boolean;
  xact_commit: number | null;
  xact_rollback: number | null;
  blks_read: number | null;
  blks_hit: number | null;
  cache_hit_ratio: number | null;
  tup_inserted: number | null;
  tup_updated: number | null;
  tup_deleted: number | null;
  temp_bytes: number | null;
  active_time_ms: number | null;
}

export interface TrafficDay {
  day: string;
  requests: number;
  status_4xx: number;
  status_5xx: number;
  error_rate_5xx: number | null;
  error_rate_4xx: number | null;
  /** THIS tenant's p95 (its own histogram) — never an average across tenants. */
  p95_ms: number | null;
  p95_capped: boolean;
}

export interface TrafficByApp {
  route_prefix: string;
  app: string;
  requests: number;
  status_4xx: number;
  status_5xx: number;
  error_rate_5xx: number | null;
  p95_ms: number | null;
  p95_capped: boolean;
}

export interface BasisValue {
  value: number | null;
  unit: string;
  basis: 'estimated' | 'measured' | string;
}

export interface ComputeShare {
  basis: 'estimated';
  method: string;
  request_share: BasisValue;
  db_active_time_share: BasisValue;
  cpu_cores: BasisValue;
  memory_bytes: BasisValue;
  app_tier_cpu_cores: BasisValue;
  db_tier_cpu_cores: BasisValue;
  inputs: {
    environment_requests: BasisValue;
    tenant_requests: BasisValue;
    app_tier_cpu_cores: BasisValue;
    app_tier_memory_bytes: BasisValue;
    db_tier_cpu_cores: BasisValue;
    db_tier_memory_bytes: BasisValue;
    environment_db_active_time_ms: BasisValue;
    tenant_db_active_time_ms: BasisValue;
  };
}

/** One live panel of `/products`: each service degrades on its own (CP2). */
export interface LivePanel<T = unknown> {
  service: string;
  status: 'ok' | 'unavailable' | 'not_configured' | 'error';
  panel?: string | null;
  reason?: string | null;
  http_status?: number | null;
  error_code?: string | null;
  data?: T | null;
}

export interface AiUsageFeature {
  feature: string;
  calls: number;
  input_units: number;
  output_units: number;
  provider_cost_credits: number;
  credits_debited: number;
}

export interface MessagingUsageRow {
  day: string;
  purpose: string;
  messages: number;
  parts: number;
  verifications: number;
  delivered: number;
  failed: number;
  expired: number;
  cost_credits: number;
  price_credits: number;
}

/** A product-local wallet as its service answers it — micro-credits (display with billing's scale). */
export interface WalletBalance {
  balance_credits: number;
  held_credits?: number | null;
  leased_credits?: number | null;
  available_credits?: number | null;
  /** Messaging only: a `postpaid` account is never refused for an empty wallet — it runs overdrawn. */
  credit_mode?: 'prepaid' | 'postpaid' | string | null;
  overdrawn?: boolean | null;
  /** Messaging only: `shared` (children spend this wallet) or `per_child` (each child has its own — see `children`). */
  wallet_mode?: string | null;
  /** Messaging only: billing's scale as messaging read it; null = UNKNOWN (never assume 100). */
  credit_display_scale?: number | null;
  /** Messaging only: each child account's own wallet (`per_child`); null otherwise. */
  children?: ChildWallet[] | null;
}

/** One child account's own communications wallet (messaging `per_child` mode). Micro-credits. */
export interface ChildWallet {
  account_id: string;
  external_ref: string | null;
  name: string;
  status: string;
  balance_credits: number;
  held_credits: number;
  leased_credits: number;
  available_credits: number;
  overdrawn: boolean;
}

/** `GET …/tenants/{id}/credits` — LIVE, each wallet on its own (CP2); nothing is stored. */
export interface TenantCredits {
  tenant_id: string;
  environment: string;
  generated_at: string;
  ai: LivePanel<WalletBalance>;
  messaging: LivePanel<WalletBalance>;
}

export interface Products {
  tenant_id: string;
  environment: string;
  period: string;
  generated_at: string;
  basis: string;
  billing: LivePanel<RatedUsage>;
  ai: LivePanel<{ features: AiUsageFeature[] }>;
  messaging: LivePanel<{ from: string; to: string; rows: MessagingUsageRow[]; totals: MessagingUsageRow }>;
}

// ─── Environment health (§6) ────────────────────────────────────────────────

export interface DiskView {
  mount: string;
  filesystem: string | null;
  size_bytes: number | null;
  used_bytes: number | null;
  available_bytes: number | null;
  free_percent: number | null;
}

export interface HostView {
  host: string;
  last_sample_at: string | null;
  stale: boolean;
  cpu_count: number | null;
  load1: number | null;
  load5: number | null;
  load15: number | null;
  mem_total_bytes: number | null;
  mem_used_bytes: number | null;
  mem_available_bytes: number | null;
  swap_total_bytes: number | null;
  swap_used_bytes: number | null;
  disks: DiskView[] | null;
  sections: Record<string, string> | null;
  collection: {
    window_hours: number;
    expected_samples: number;
    received_samples: number;
    missing_samples: number;
    coverage: number | null;
  };
}

export interface ContainerView {
  host: string;
  container: string;
  service: string | null;
  compose_project: string | null;
  last_sample_at: string;
  stale: boolean;
  cpu_percent: number | null;
  cpu_percent_max: number | null;
  cpu_limit_cores: number | null;
  /** Null when the container has no configured CPU limit. */
  cpu_percent_of_limit: number | null;
  mem_used_bytes: number | null;
  mem_used_max_bytes: number | null;
  mem_limit_bytes: number | null;
  mem_percent_of_limit: number | null;
  metrics: Record<string, unknown> | null;
}

export interface KafkaGroupView {
  container: string;
  group: string;
  lag: number | null;
  last_sample_at: string;
  /** Latest raw samples, oldest first; null = a gap. */
  recent: (number | null)[];
  growing: boolean;
}

export interface AlertView {
  kind: string;
  severity: 'warning' | 'critical' | string;
  since: string;
  host: string | null;
  target: string;
  value: number | null;
  threshold: number | null;
  detail: string;
}

export interface Overview {
  environment: string;
  label: string;
  generated_at: string;
  window_hours: number;
  basis: string;
  hosts: HostView[];
  containers: ContainerView[];
  kafka: KafkaGroupView[];
  alerts: AlertView[];
}

export type SeriesMetric =
  | 'load1' | 'mem_used_bytes' | 'mem_used_percent' | 'swap_used_bytes' | 'disk_used_percent'
  | 'container_cpu_percent' | 'container_mem_used_bytes' | 'kafka_lag';

export interface SeriesPoint {
  t: string;
  /** null = a bucket with no sample — drawn as a gap. */
  value: number | null;
  samples: number;
  expected: number;
}

export interface Series {
  environment: string;
  metric: string;
  host: string | null;
  container: string | null;
  group: string | null;
  mount: string | null;
  resolution: string;
  bucket_seconds: number;
  from: string;
  to: string;
  expected_samples: number;
  received_samples: number;
  points: SeriesPoint[];
}

// ─── Billing admin (proxied, Billing.Contracts) ────────────────────────────

export interface BillingSettings {
  default_currency: string;
  default_tax_kind: string;
  default_tax_rate_bps: number;
  tax_label_en: string;
  tax_label_ar: string;
  prices_ex_tax: boolean;
  invoice_prefix: string;
  invoice_next_seq: number;
  rounding: string;
  /** A DISPLAY transform: displayed credits = micro-credits ÷ this. Never a stored figure (D-CP21). */
  credit_display_scale: number;
  credit_reference_currency: string;
  dunning_schedule_days: number[];
  /** Additive (2026-10-14): the service fee on every credit purchase, in basis points (200 = 2 %). Absent on older billing. */
  purchase_service_fee_bps?: number | null;
  /** Additive (2026-10-14): the smallest custom purchase, in DISPLAYED credits. */
  min_purchase_credits?: number | null;
  /** Additive (2026-10-14): the largest custom purchase, in DISPLAYED credits (≥ the minimum). */
  max_purchase_credits?: number | null;
  updated_at: string;
  updated_by: string | null;
}

export interface FxRate {
  id: string;
  currency: string;
  units_per_reference: number;
  effective_from: string;
  needs_review: boolean;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

export interface FxRateList {
  reference_currency: string;
  fx_rates: FxRate[];
}

export interface BillingAccount {
  tenant_id: string;
  kind: string;
  name: string | null;
  environment: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface BillingProfile {
  tenant_id: string;
  currency: string | null;
  tax_rate_bps: number | null;
  tax_exempt_reason: string | null;
  tax_registration_id: string | null;
  legal_name: string | null;
  address: unknown;
  invoice_language: string;
  payment_terms_days: number;
  notify_user_ids: string[];
  effective_currency: string;
  effective_tax_rate_bps: number;
  updated_at: string | null;
}

export interface CostCard {
  id: string;
  product: string;
  provider_key: string;
  category: string;
  country: string;
  network: string;
  unit: string;
  /** Rounded half-up to whole minor units (legacy) — `cost_amount` is the exact figure. */
  cost_amount_minor: number;
  /** The exact cost in MAJOR units as a decimal string, up to 6 places (`"0.0036"`) — C10; absent on older billing. */
  cost_amount?: string | null;
  cost_currency: string;
  cost_credits: number | null;
  valid_from: string;
  valid_to: string | null;
  version: number;
  effective: boolean;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

/** `GET cost-cards/vocabulary` (C10): the units and categories each product's cost cards take. */
export interface CostCardVocabulary {
  products: {
    product: string;
    units: string[];
    categories: string[];
    /** Optional refinement: the categories valid for one unit (SMS `verification` takes channels, not purposes). */
    categories_by_unit?: Record<string, string[]> | null;
  }[];
}

/** `GET currencies` (C10): billing's minor-unit digits per ISO code — both front ends format money with it, never Intl. */
export interface CurrencyTable {
  currencies: { code: string; minor_digits: number }[];
}

export interface PricingRule {
  product: string;
  default_markup_bps: number;
  updated_at: string;
  updated_by: string | null;
}

export interface PriceBookVersion {
  id: string;
  status: 'draft' | 'published' | string;
  effective_from: string | null;
  note: string | null;
  copied_from_version_id: string | null;
  created_by: string | null;
  created_at: string;
  published_at: string | null;
  published_by: string | null;
  item_count: number;
  in_force: boolean;
}

export interface PriceBookItem {
  sku: string;
  /** ISO currency, or `CREDITS` for a consumption rate (amount in micro-credits). */
  currency: string;
  unit: string;
  amount: number;
  metadata: Record<string, unknown> | null;
}

export interface PriceBookVersionDetail {
  version: PriceBookVersion;
  prices: PriceBookItem[];
}

/** One SMS / WhatsApp message type's price (billing `GET message-prices`). Amounts are micro-credits. */
export interface MessagePriceRow {
  product: 'sms' | 'whatsapp';
  /** SMS: transactional | marketing | otp; WhatsApp: service | utility | marketing | authentication. */
  type: string;
  /** `part` (an SMS part) | `message` (a WhatsApp message). */
  unit: string;
  sku: string;
  /** null = unpriced (sends of this type are refused). */
  price_credits: number | null;
  price_display: string | null;
  source: 'override' | 'book' | 'default' | 'unpriced' | string;
  explanation: string;
  book_price_credits: number | null;
  cost_credits: number | null;
  margin_credits: number | null;
  /** Every country with a cost card or a book / override row (C10). */
  country_prices: MessageCountryPrice[];
  /** False = no cost card covers this type anywhere: our cost is unknown and cost + markup cannot price it. */
  has_cost_card?: boolean;
  /** The highest cost over the countries this any-country price covers — the below-cost check (C10). */
  max_cost_credits?: number | null;
}

/** One destination country of a message type (C10). Micro-credits. */
export interface MessageCountryPrice {
  country: string;
  /** null = unpriced in that country. */
  price_credits: number | null;
  source: 'book' | 'override' | 'cost_markup' | 'unpriced' | string;
  cost_credits?: number | null;
  margin_credits?: number | null;
  /** The platform's own book row for this country; null = none (cost + markup or the any-country row applies). */
  book_price_credits?: number | null;
  price_display?: string | null;
}

export interface MessagePrices {
  tenant_id: string | null;
  /** null = UNKNOWN — never assume 100. */
  credit_display_scale: number | null;
  price_book_version_id: string | null;
  price_book_effective_from: string | null;
  scheduled_version_from: string | null;
  open_drafts: number;
  sms: MessagePriceRow[];
  whatsapp: MessagePriceRow[];
}

export interface MessagePriceInput {
  product: 'sms' | 'whatsapp';
  type: string;
  /** ISO-2, or `*` (the default) for every country. */
  country?: string;
  /** null REMOVES the book row: the type (in that country) goes back to cost + markup (C10). */
  price_credits: number | null;
  /** Explicit consent to a price below our highest cost (else 422 PRICE_BELOW_COST). */
  allow_below_cost?: boolean;
}

export interface PriceBookDiff {
  from: string;
  to: string;
  added: PriceBookItem[];
  removed: PriceBookItem[];
  changed: {
    sku: string;
    currency: string;
    from_amount: number;
    to_amount: number;
    from_metadata: Record<string, unknown> | null;
    to_metadata: Record<string, unknown> | null;
  }[];
}

export interface PriceOverride {
  id: string;
  tenant_id: string;
  sku: string;
  currency: string;
  amount: number | null;
  markup_bps: number | null;
  reason: string;
  starts_at: string;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  expired_at: string | null;
  expired_by: string | null;
  active: boolean;
}

export interface Offer {
  id: string;
  code: string | null;
  name: string;
  kind: 'percent' | 'fixed' | 'bonus_credits' | 'free_months' | string;
  value: number;
  currency: string | null;
  applies_to_kind: string;
  applies_to: string[];
  eligibility: string;
  eligible_tenant_ids: string[];
  eligible_plan_codes: string[];
  starts_at: string;
  ends_at: string | null;
  max_redemptions: number | null;
  per_tenant_limit: number | null;
  stackable: boolean;
  redemptions_count: number;
  state: string;
  created_by: string | null;
  created_at: string;
  expired_at: string | null;
  expired_by: string | null;
}

export interface OfferRedemption {
  id: string;
  offer_id: string;
  tenant_id: string;
  line_ref: string;
  sku: string;
  invoice_id: string | null;
  purchase_id: string | null;
  discount_minor: number;
  currency: string | null;
  bonus_credits: number;
  redeemed_at: string;
}

export interface OfferRedemptionList {
  offer_id: string;
  redemptions_count: number;
  max_redemptions: number | null;
  redemptions: OfferRedemption[];
  next_cursor: string | null;
}

export interface RetentionPolicy {
  data_class: string;
  default_days: number;
  /**
   * The rollout switch (D-CP23 / R20): purge workers delete data for this class ONLY when true. Null or
   * absent is NOT enforced (fail-safe) — never render a missing flag as enforced. Classes holding legacy
   * data ship not enforced; an admin enables each after review, with a reason (billing audits it).
   */
  enforced?: boolean | null;
  floor_days: number | null;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

/** One class in PUT retention/policies. `enforced` is sent only when the admin changed it. */
export interface RetentionPolicyUpdate {
  data_class: string;
  default_days: number;
  enforced?: boolean;
}

export interface RetentionPolicyList {
  policies: RetentionPolicy[];
  allowed: number[];
}

export interface RetentionOverride {
  tenant_id: string;
  data_class: string;
  days: number;
  reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RetentionRun {
  id: string;
  service: string;
  data_class: string;
  tenant_id: string | null;
  rows_purged: number;
  rows_rolled_up: number;
  days_applied: number | null;
  status: string;
  note: string | null;
  ran_at: string;
}

/** One rated line (§4.5) — credits first, then the money equivalent at the current pack price. */
export interface RatedLine {
  group: string;
  label_en: string;
  label_ar: string;
  quantity: number;
  unit: string;
  credits: number;
  credits_display: string;
  /** Money equivalent at the current pack price in force; null when it could not be priced. */
  price_minor: number | null;
  currency: string;
  cost_credits: number;
  margin_credits: number;
  explanation: string;
}

export interface RatedUsage {
  tenant_id: string;
  period: string;
  currency: string;
  /** null = UNKNOWN — never assume 100. */
  credit_display_scale: number | null;
  lines: RatedLine[];
  totals: {
    credits: number;
    credits_display: string;
    price_minor: number;
    cost_credits: number;
    margin_credits: number;
  };
}

export interface ProjectionItem {
  product: 'ai' | 'whatsapp' | 'sms' | 'payments';
  feature?: string;
  category?: string;
  country?: string;
  quantity: number;
  unit?: string;
  credits_per_unit?: number;
  cost_credits_per_unit?: number;
  provider_key?: string;
}

export interface ProjectionBody {
  period?: string;
  plan_code?: string;
  billing_period?: string;
  seats?: number;
  storage_gb?: number;
  volumes: ProjectionItem[];
}

export interface Projection {
  projection: RatedUsage;
  assumptions: {
    product: string;
    key: string;
    quantity: number;
    credits_per_unit: number;
    cost_credits_per_unit: number;
    explanation: string;
  }[];
}

export interface PriceExplanation {
  tenant_id: string;
  sku: string;
  at: string;
  resolution: {
    sku: string;
    amount: number;
    currency: string;
    source: string;
    explanation: string;
    price_book_version_id: string | null;
    override_id: string | null;
    offer_id: string | null;
    base_amount: number;
    discount_amount: number;
    bonus_credits: number;
    free_months: number;
    cost_credits: number | null;
    markup_bps: number | null;
  };
}

export interface ProductCost {
  product: string;
  credits: number;
  cost_credits: number;
  margin_credits: number;
  /** Negative when the cost exceeds the price; null when something cost us money but nothing was charged (money review). */
  margin_bps: number | null;
  events: number;
}

/** One group of billing's grants ledger in the costs summary: credits given vs money received (money review 2026-10-04). */
export interface CreditGrantSummary {
  wallet: string;
  kind: string;
  currency: string | null;
  count: number;
  /** Micro-credits. */
  credits: number;
  amount_minor: number;
  shortfall_credits: number;
}

export interface CostsSummary {
  period: string;
  /** null = UNKNOWN — never assume 100. */
  credit_display_scale: number | null;
  /** The platform's own sales — BYO WhatsApp (the tenant pays Meta) is NOT in here; it is `byo_products`. */
  products: ProductCost[];
  totals: ProductCost;
  top_tenants: { tenant_id: string; name: string | null; credits: number; cost_credits: number; margin_credits: number }[];
  trend: { period: string; credits: number; cost_credits: number; margin_credits: number }[];
  /** Additive (money review): the grants ledger for the period. */
  grants?: CreditGrantSummary[] | null;
  /** Additive (money review C2): BYO WhatsApp, reported apart — not our revenue or our cost. */
  byo_products?: ProductCost[] | null;
}

// ─── Money dashboard (billing GET money/summary, 2026-10-14) ───────────────

export type MoneyTestFilter = 'include' | 'exclude' | 'only';
export type MoneyGranularity = 'day' | 'month';

/**
 * The platform's money for a window. Money = integer minor units of `currency`; every credit figure is MICRO-credits
 * (divide by `credit_display_scale`). Built by billing from paid invoices + credit notes — the console only renders it.
 */
export interface MoneySummary {
  currency: string;
  from: string;
  to: string;
  granularity: MoneyGranularity;
  test: MoneyTestFilter;
  generated_at: string;
  totals: MoneyTotals;
  /** One row per period in the window, zeros included, ascending. */
  series: MoneySeriesPoint[];
  by_wallet: MoneyByWallet[];
  by_status: { status: string; count: number; amount_minor: number }[];
  by_gateway: MoneyByGateway[];
  top_tenants: MoneyTopTenant[];
  outstanding_invoices: { issued_count: number; issued_minor: number; overdue_count: number; overdue_minor: number };
  /** All time (not windowed); always an estimate. */
  liability: MoneyLiability[];
  /** null = unknown — never assume a scale. */
  credit_display_scale: number | null;
}

/** `GET money/summary` query: ISO instants `[from, to)` (≤ 400 days), billing's default currency when absent. */
export interface MoneySummaryFilter {
  from: string;
  to: string;
  granularity?: MoneyGranularity;
  currency?: string;
  test?: MoneyTestFilter;
}

/** `GET payments` filters (admin). `from` / `to` are ISO instants on created_at. */
export interface PaymentListFilter {
  tenant_id?: string;
  status?: string;
  purpose?: string;
  from?: string;
  to?: string;
  test_mode?: boolean;
  gateway_row_id?: string;
  limit?: number;
  cursor?: string | null;
}

export interface MoneyTotals {
  gross_collected_minor: number;
  credit_sales_minor: number;
  service_fees_minor: number;
  subscription_minor: number;
  other_minor: number;
  vat_minor: number;
  refunds_minor: number;
  net_after_refunds_minor: number;
  purchases_count: number;
  paying_tenants: number;
  average_purchase_minor: number;
  /** Micro-credits. */
  credits_sold: number;
  /** Micro-credits. */
  credits_granted_free: number;
  failed_payments: number;
  pending_payments: number;
}

export interface MoneySeriesPoint {
  period: string;
  gross_collected_minor: number;
  credit_sales_minor: number;
  service_fees_minor: number;
  vat_minor: number;
  refunds_minor: number;
  purchases_count: number;
}

export interface MoneyByWallet {
  wallet: string;
  /** Micro-credits. */
  credits_sold: number;
  /** Micro-credits. */
  credits_granted_free: number;
  sales_minor: number;
  purchases_count: number;
}

export interface MoneyByGateway {
  gateway_row_id: string | null;
  gateway_key: string;
  display_name: string | null;
  test_mode: boolean;
  count: number;
  succeeded_minor: number;
  failed_count: number;
}

export interface MoneyTopTenant {
  tenant_id: string;
  name: string | null;
  gross_minor: number;
  purchases_count: number;
  last_paid_at: string | null;
}

export interface MoneyLiability {
  wallet: string;
  /** Micro-credits, all kinds of grant. */
  granted_credits: number;
  /** Micro-credits. */
  consumed_credits: number;
  /** Micro-credits. */
  unused_credits: number;
  /** Unused displayed credits × the current unit price — an estimate. */
  unused_value_minor: number | null;
  estimate: boolean;
}

export interface Invoice {
  id: string;
  tenant_id: string;
  kind: string;
  number: string | null;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'void' | string;
  period: string | null;
  currency: string;
  subtotal_minor: number;
  discount_minor: number;
  tax_rate_bps: number;
  tax_minor: number;
  total_minor: number;
  tax_label_en: string | null;
  tax_label_ar: string | null;
  invoice_language: string | null;
  legal_name: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  purchase_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLine {
  line_no: number;
  sku: string;
  description_en: string | null;
  description_ar: string | null;
  quantity: number;
  unit: string | null;
  unit_amount_minor: number;
  discount_minor: number;
  amount_minor: number;
  price_book_version_id: string | null;
  override_id: string | null;
  offer_id: string | null;
  explanation: string | null;
}

export interface InvoiceDetail {
  invoice: Invoice;
  lines: InvoiceLine[];
}

export interface InvoicePaymentLink {
  invoice_id: string;
  payment_id: string;
  checkout_url: string;
  total_minor: number;
  currency: string;
}

export interface Payment {
  id: string;
  tenant_id: string;
  scope: string;
  gateway_row_id: string | null;
  gateway_key: string;
  purpose: string;
  status: string;
  amount_minor: number;
  currency: string;
  refunded_minor: number;
  method: string | null;
  purchase_id: string | null;
  invoice_id: string | null;
  ext_intent_id: string | null;
  external_id: string | null;
  failure_reason: string | null;
  /** Paid through a gateway row in test mode (billing's PaymentDto). Absent on older billing = unknown, never "live". */
  test_mode?: boolean | null;
  /** Additive (2026-10-14): the tenant's name on admin lists; null when billing does not know it. */
  tenant_name?: string | null;
  created_at: string;
  updated_at: string;
  settled_at: string | null;
}

export interface Purchase {
  id: string;
  tenant_id: string;
  account_id: string | null;
  wallet: string;
  pack_sku: string;
  credits: number;
  credits_display: string;
  bonus_credits: number;
  currency: string;
  subtotal_minor: number;
  discount_minor: number;
  /** Additive (2026-10-14): the service fee rate and amount; null on purchases made before the fee existed. */
  service_fee_bps?: number | null;
  service_fee_minor?: number | null;
  tax_rate_bps: number;
  tax_minor: number;
  total_minor: number;
  status: string;
  channel: string;
  payment_id: string | null;
  invoice_id: string | null;
  grant_attempts: number;
  next_grant_attempt_at: string | null;
  last_grant_error: string | null;
  price_book_version_id: string | null;
  override_id: string | null;
  offer_id: string | null;
  created_at: string;
  updated_at: string;
  paid_at: string | null;
  granted_at: string | null;
}

export interface ManualGrantResult {
  tenant_id: string;
  wallet: string;
  credits: number;
  balance_credits: number;
  amount_granted_credits: number;
  already_applied: boolean;
  idempotency_key: string;
}

export interface PaymentGateway {
  id: string;
  gateway_key: string;
  /** null = a platform row (our own sales); set = a BYO row owned by that tenant. */
  owner_tenant_id: string | null;
  scope: string;
  display_name: string;
  enabled: boolean;
  retired: boolean;
  priority: number;
  currencies: string[];
  methods: string[];
  test_mode: boolean;
  /** `unavailable` when no adapter is registered for the key (R10). */
  status: string;
  adapter_registered: boolean;
  /** Secrets are REDACTED to presence flags (BL5). */
  secrets: Record<string, boolean>;
  config_public: Record<string, unknown> | null;
  last_verified_at: string | null;
  webhook_url: string | null;
  return_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface GatewayTestResult {
  id: string;
  ok: boolean;
  status: string;
  detail: string | null;
  last_verified_at: string | null;
}

export interface ApiKey {
  key_id: string;
  tenant_id: string;
  account_id: string | null;
  kind: 'secret' | 'publishable' | string;
  name: string;
  display: string;
  scopes: string[];
  allowed_origins: string[];
  rate_limit_per_minute: number;
  sandbox: boolean;
  status: string;
  created_by: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  expires_at: string | null;
  rotated_from_key_id: string | null;
  rotated_to_key_id: string | null;
}

/** Create / rotate answer — `key` is the plaintext, shown ONCE and never retrievable again. */
export interface ApiKeySecret {
  api_key: ApiKey;
  key: string;
}

export interface ExtWebhookEndpoint {
  id: string;
  tenant_id: string;
  url: string;
  events: string[];
  enabled: boolean;
  secret_set: boolean;
  created_at: string;
  updated_at: string;
  rotated_at: string | null;
}

/** `signing_secret` is present ONLY on the answer that created or rotated it. */
export interface ExtWebhookResponse {
  endpoint: ExtWebhookEndpoint | null;
  signing_secret: string | null;
}

export interface AdminAuditEntry {
  id: string;
  actor_id: string;
  actor_name: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  tenant_id: string | null;
  reason: string | null;
  before: unknown;
  after: unknown;
  at: string;
  /**
   * Billing only: 'security' for credential / API-key / gateway / ext-webhook writes, 'info' otherwise. Null or
   * absent (older rows, messaging's log) is info.
   */
  severity?: AuditSeverity | string | null;
}

/** Billing's audit-log severity filter (`?severity=`); any other value is a 400, so only these are ever sent. */
export type AuditSeverity = 'info' | 'security';

export interface AdminAuditLog {
  entries: AdminAuditEntry[];
  next_cursor: string | null;
}

// ─── Messaging admin (proxied, Messaging.Contracts) ────────────────────────

export interface MessagingProvider {
  key: string;
  kind: 'sms' | 'whatsapp' | string;
  display_name: string;
  enabled: boolean;
  account_mode: 'prepaid' | 'postpaid' | string;
  dlr_mode: 'webhook' | 'poll' | 'none' | string;
  endpoints: Record<string, unknown> | null;
  /** Presence only — credentials are write-only (MS5). */
  credentials_set: boolean;
  credentials_updated_at: string | null;
  credentials_public: Record<string, unknown> | null;
  capabilities: Record<string, unknown> | null;
  dlr_ip_allowlist: string[];
  reconcile_after_minutes: number;
  expire_after_hours: number;
  notes: string | null;
  adapter_registered: boolean;
  updated_by: string;
  updated_at: string;
}

export interface MessagingRoute {
  id: string;
  country: string;
  purpose: string | null;
  sender_id: string | null;
  provider_key: string;
  priority: number;
  enabled: boolean;
  updated_by: string;
  updated_at: string;
}

export interface ComplianceProfile {
  country: string;
  marketing_window_start: string;
  marketing_window_end: string;
  time_zone: string;
  max_promotional_per_recipient_per_day: number;
  stop_keywords: string[];
  help_keywords: string[];
  opt_out_line_en: string;
  opt_out_line_ar: string;
  opt_out_sla_hours: number;
  updated_by: string;
  updated_at: string;
}

export interface LegacyConcept {
  concept: string;
  match_mode: 'substring' | 'exact' | string;
  precedence: number;
  sender_id: string;
  network_overrides: { national_prefix: string; sender_id: string }[];
  account_external_ref: string;
  verify_template_code: string | null;
  enabled: boolean;
  note: string | null;
  updated_by: string;
  updated_at: string;
}

export interface SenderId {
  id: string;
  tenant_id: string;
  account_id: string | null;
  value: string;
  networks: string[];
  countries: string[];
  registration_status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'suspended' | string;
  documents: unknown;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejected_reason: string | null;
  suspended_at: string | null;
  suspended_reason: string | null;
  is_default: boolean;
  provider_key: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ProviderSpend {
  from: string;
  to: string;
  days: {
    day: string;
    provider_key: string;
    messages: number;
    parts: number;
    verifications: number;
    cost_credits: number;
    delivered: number;
    failed: number;
    expired: number;
  }[];
  total_cost_credits: number;
  payments: ProviderPayment[];
}

export interface ProviderPayment {
  id: string;
  provider_key: string;
  amount_minor: number;
  currency: string;
  paid_at: string;
  reference: string | null;
  note: string | null;
  created_by: string;
  created_at: string;
}

export interface DlrStats {
  from: string;
  to: string;
  providers: {
    provider_key: string;
    sent: number;
    delivered: number;
    failed: number;
    expired: number;
    pending: number;
    median_dlr_latency_ms: number | null;
  }[];
  compliance?: { missing_compliance_profiles: string[]; catch_all_route_without_profile: boolean } | null;
}

export interface MessagingAccount {
  account_id: string;
  kind: 'parent' | 'child' | string;
  parent_id: string | null;
  external_ref: string | null;
  name: string;
  default_sender_id: string | null;
  wallet_mode: string | null;
  tenant_kind: string | null;
  credit_mode: 'prepaid' | 'postpaid' | string;
  monthly_cap_parts: number | null;
  status: string;
  created_at: string;
}

export interface MessagingGrantResult {
  balance_credits: number;
  amount_granted_credits: number;
  already_applied: boolean;
}

export interface CreditModeResult {
  tenant_id: string;
  credit_mode: string;
  changed: boolean;
}

export interface CampaignTotals {
  audience: number;
  pending: number;
  skipped: number;
  skipped_by_reason: Record<string, number>;
  cancelled: number;
  failed: number;
  accepted: number;
  sent: number;
  delivered: number;
  delivery_failed: number;
  expired: number;
  parts: number;
}

export interface Campaign {
  campaign_id: string;
  account_id: string;
  name: string;
  template_id: string | null;
  template_version: number | null;
  sender_id: string | null;
  purpose: string;
  audience_source: string;
  status: string;
  status_reason: string | null;
  scheduled_at: string | null;
  throttle_per_minute: number;
  respect_quiet_hours: boolean;
  estimated_parts: number | null;
  estimated_price_credits: number | null;
  source: string;
  source_ref: string | null;
  launched_by: string | null;
  launched_at: string | null;
  completed_at: string | null;
  created_at: string;
  totals: CampaignTotals | null;
}

export interface CampaignList {
  campaigns: Campaign[];
  next_cursor: string | null;
}

// ─── Tenant setup check (2026-09-30) ───────────────────────────────────────

export type SetupCheckStatus = 'ok' | 'red' | 'not_set_up' | 'endpoint_absent' | 'unavailable' | 'not_configured' | 'error';

export interface SetupCheckServiceView {
  /** The catalog step re-run (`verify.salesiq`). */
  key: string;
  service: string;
  status: SetupCheckStatus | string;
  checks: number | null;
  failing_checks: string[];
  http_status: number | null;
  error_code: string | null;
}

/** POST …/tenants/{id}/owner-invitation/resend — whether a new link went out, and why not. */
export interface OwnerInvitationResend {
  tenant_id: string;
  sent: boolean;
  /** `no_founder` | `nothing_to_send` (the owner already registered, or declined) — only when nothing was sent. */
  reason: string | null;
}

export interface SetupCheckView {
  tenant_id: string;
  environment: string;
  kind: string;
  checked_at: string;
  /** True only when every service answered green. */
  all_ok: boolean;
  services: SetupCheckServiceView[];
}

// ─── Money review 2026-10-04 (C6 / C8 / C10) ───────────────────────────────

/**
 * billing `GET ledger/summary?tenant_id&product&from&to` (C8). Micro-credits. `rows` = every sale by feature (platform +
 * BYO); `refunded` = the refund reversals (positive); `byo` = the BYO part of `rows` (the tenant pays Meta itself).
 */
export interface LedgerSummary {
  tenant_id?: string;
  product?: string | null;
  from?: string | null;
  to?: string | null;
  rows: { feature: string; count: number; price_credits: number; cost_credits: number }[];
  refunded: { count: number; price_credits: number };
  byo?: { feature: string; count: number; price_credits: number; cost_credits: number }[] | null;
}

/** billing `GET parked-events` (C10): a ledger event billing could not apply and set aside. */
export interface ParkedEvent {
  id: string;
  source: string;
  reason: string;
  tenant_id: string | null;
  occurred_at: string | null;
  parked_at: string;
  attempts: number;
  /** A short, PII-free description of why (billing's own text). */
  detail?: string | null;
  replayed_at?: string | null;
  replay_outcome?: string | null;
}

/** billing `POST parked-events/{id}/replay`: `recorded` / `duplicate` / `ignored` (resolved) or `parked` (still parked). */
export interface ParkedEventReplay {
  id: string;
  outcome: string;
  resolved: boolean;
  attempts: number;
}

/** billing `GET payments/needs-refund` (C10): money we received that must go back (a second success, a paid-or-void target). */
export interface RefundNeededPayment {
  id: string;
  tenant_id: string;
  purchase_id: string | null;
  invoice_id: string | null;
  amount_minor: number;
  currency: string;
  gateway_key: string;
  reason: string;
  created_at: string;
}

/** billing `GET clawback-shortfalls` (C10): a refunded pack whose credits were already spent. Micro-credits. */
export interface ClawbackShortfall {
  purchase_id: string;
  tenant_id: string;
  wallet: string;
  credits_granted: number;
  clawed_back_credits: number;
  shortfall_credits: number;
  refunded_at: string | null;
}

/** ai `GET …/admin/rate-cards` (C6) — one platform rate-card version. Costs are USD per million units. */
export interface AiRateCard {
  id: string;
  provider_key: string;
  model_key: string;
  unit: string;
  input_cost_per_million_usd: number;
  output_cost_per_million_usd: number;
  margin_bps: number;
  min_charge_credits: number;
  version: string;
  active: boolean;
  created_at: string;
}

/** One local-engine tariff version (C6). `credits_per_unit` is micro-credits (may be fractional). */
export interface AiLocalTariff {
  id: string;
  task: string;
  unit: string;
  credits_per_unit: number;
  min_charge_credits: number;
  version: string;
  active: boolean;
  created_at: string;
}

export interface AiRateCards {
  cards: AiRateCard[];
  local_tariffs: AiLocalTariff[];
}

export interface CountCredits {
  count: number;
  credits: number;
}

/** salesIQ `credits/summary` (C8): its WhatsApp debits in the window. Micro-credits. */
export interface SalesIqWhatsAppSummary {
  tenant_id: string;
  from: string;
  to: string;
  sent: { count: number; credits: number; cost_credits: number };
  refunded: CountCredits;
  pending: CountCredits;
  free: { count: number };
  by_category: { category: string; count: number; credits: number }[];
  by_lease: { lease_id: string | null; count: number; credits: number }[];
  /** Additive: credits > 0 only — what messaging settles. */
  charged?: CountCredits | null;
  /** Additive: charged − refunded. */
  net?: CountCredits | null;
}

/** salesIQ `meta-pricing` (C8): Meta's recorded pricing beside our category — a record, never a charge. */
export interface MetaPricingReport {
  rows: {
    our_category: string | null;
    meta_category: string | null;
    meta_billable: boolean | null;
    meta_type: string | null;
    count: number;
    credits: number;
  }[];
}

/** messaging `tenants/{id}/whatsapp/consumptions` (C8). Micro-credits. */
export interface WhatsAppConsumptions {
  consumed: CountCredits;
  refunded: CountCredits;
  late: CountCredits;
  by_lease: { lease_id: string | null; count: number; credits: number; refunded_credits?: number | null }[];
  /** Additive: consumed − refunded. */
  net?: CountCredits | null;
  over_consumption?: CountCredits | null;
}

/** `GET …/tenants/{id}/whatsapp/salesiq-summary|meta-pricing` — one salesIQ read as a live panel (CP2). */
export interface TenantWhatsAppPanel<T> {
  tenant_id: string;
  environment: string;
  /** Whole UTC days, inclusive. */
  from: string;
  to: string;
  generated_at: string;
  salesiq: LivePanel<T>;
}
